import {Component, Input, OnDestroy, OnInit} from '@angular/core';

interface HumanityValueFormat {
  next: string;
  prev: string;
  format: (diff: number) => string;
}

interface HumanityValueFormats {
  [key: string]: HumanityValueFormat;
}

const HUMANITY_VALUE_FORMATS: HumanityValueFormats = {
  year: {
    next: `в следующем году`,
    prev: `в прошлом году`,
    format: (diff) => {
      if (2 <= diff && diff <= 4) {
        return `года`;
      } else if (5 <= diff) {
        return `лет`;
      }
      return ``;
    },
  },
  month: {
    next: `в следующем месяце`,
    prev: `в прошлом месяце`,
    format: diff => {
      if (2 <= diff && diff <= 4) {
        return `месяца`;
      } else if (5 <= diff) {
        return `месяцев`;
      }
      return ``;
    },
  },
  week: {
    next: `на следующей неделе`,
    prev: `на прошлой неделе`,
    format: diff => {
      if (2 <= diff && diff <= 4) {
        return `недели`;
      } else if (5 <= diff) {
        return `недель`;
      }
      return ``;
    },
  },
  day: {
    next: `завтра`,
    prev: `вчера`,
    format: diff => {
      if ((2 <= diff && diff <= 4)
        || (22 <= diff && diff <= 24)
        || (32 <= diff && diff <= 34)) {
        return `дня`;
      } else if ((5 <= diff && diff <= 20)
        || (25 <= diff && diff <= 30)) {
        return `дней`;
      } else if (21 === diff || 31 === diff) {
        return `день`;
      }
      return ``;
    },
  },
  hour: {
    next: `через час`,
    prev: `час назад`,
    format: diff => {
      if ((2 <= diff && diff <= 4)
        || (22 <= diff && diff <= 24)) {
        return `часа`;
      } else if (5 <= diff && diff <= 20) {
        return `часов`;
      } else if (21 === diff) {
        return `час`;
      }
      return ``;
    },
  },
  minute: {
    next: `через минуту`,
    prev: `минуту назад`,
    format: diff => {
      if ((2 <= diff && diff <= 4)
        || (22 <= diff && diff <= 24)
        || (32 <= diff && diff <= 34)
        || (42 <= diff && diff <= 44)
        || (52 <= diff && diff <= 54)) {
        return `минуты`;
      } else if ((5 <= diff && diff <= 20)
        || (25 <= diff && diff <= 30)
        || (35 <= diff && diff <= 40)
        || (45 <= diff && diff <= 50)
        || (55 <= diff && diff <= 60)) {
        return `минут`;
      } else if ((21 === diff)
        || (31 === diff)
        || (41 === diff)
        || (51 === diff)) {
        return `минуту`;
      }
      return ``;
    },
  },
  second: {
    next: `через секунду`,
    prev: `секунду назад`,
    format: diff => {
      // if ((2 <= diff && diff <= 4)
      //   || (22 <= diff && diff <= 24)
      //   || (32 <= diff && diff <= 34)
      //   || (42 <= diff && diff <= 44)
      //   || (52 <= diff && diff <= 54)) {
      //   return `секунды`;
      // } else if ((5 <= diff && diff <= 20)
      //   || (25 <= diff && diff <= 30)
      //   || (35 <= diff && diff <= 40)
      //   || (45 <= diff && diff <= 50)
      //   || (55 <= diff && diff <= 60)) {
      //   return `секунд`;
      // } else if ((21 === diff)
      //   || (31 === diff)
      //   || (41 === diff)
      //   || (51 === diff)) {
      //   return `секунду`;
      // }
      return `менее минуты`;
    },
  },
};

function getHumanityValue(diff: number, format: HumanityValueFormat): string {
  const absDiff = Math.abs(diff);
  let result = `${absDiff} ${format.format(absDiff)}`;
  if (result.indexOf(`менее минуты`) >= 0) {
    result = `менее минуты`;
  }
  if (diff < 0) {
    result = `${result} назад`;
  } else if (diff > 0) {
    result = `через ${result}`;
  }
  return result;
}

function getHumanityCrement(direction: number, format: HumanityValueFormat): string {
  if (direction < 0) {
    return format.prev;
  } else {
    return format.next;
  }
}

function getHumanityDate(date: Date, today: Date): string {

  const todayInMs = today.getTime();
  const dateInMs = date.getTime();
  const diffInMs = dateInMs - todayInMs;

  const diffInYears = Math.trunc(diffInMs / (1000 * 60 * 60 * 24 * 366));
  if (Math.abs(diffInYears) > 0) {
    const yearsDiff = date.getFullYear() - today.getFullYear();
    const yearsAbsDiff = Math.abs(yearsDiff);
    if (yearsAbsDiff === 1) {
      return getHumanityCrement(yearsDiff, HUMANITY_VALUE_FORMATS.year);
    } else if (yearsAbsDiff < 10) {
      return getHumanityValue(yearsDiff, HUMANITY_VALUE_FORMATS.year);
    } else {
      return date.toLocaleString();
    }
  }

  const diffInMonths = Math.trunc(diffInMs / (1000 * 60 * 60 * 24 * 31));
  if (Math.abs(diffInMonths) > 0) {
    const monthsDiff = (date.getFullYear() - today.getFullYear()) * 12 + date.getMonth() - today.getMonth();
    const monthsAbsDiff = Math.abs(monthsDiff);
    if (monthsAbsDiff === 1) {
      return getHumanityCrement(monthsDiff, HUMANITY_VALUE_FORMATS.month);
    } else {
      return getHumanityValue(monthsDiff, HUMANITY_VALUE_FORMATS.month);
    }
  }

  const diffInWeeks = Math.trunc(diffInMs / (1000 * 60 * 60 * 24 * 7));
  const aWeeks = Math.abs(diffInWeeks);
  if (aWeeks > 0) {
    const currentMonday = new Date(today.getFullYear(), today.getMonth(), (today.getDate() - (today.getDay() + 6) % 7));
    const dateMonday = new Date(date.getFullYear(), date.getMonth(), (date.getDate() - (date.getDay() + 6) % 7));
    const weeksDiff = Math.trunc((dateMonday.getTime() - currentMonday.getTime()) / (1000 * 60 * 60 * 24 * 7));
    const aWeeksDiff = Math.abs(weeksDiff);
    if (aWeeksDiff === 1) {
      return getHumanityCrement(weeksDiff, HUMANITY_VALUE_FORMATS.week);
    } else {
      return getHumanityValue(weeksDiff, HUMANITY_VALUE_FORMATS.week);
    }
  }

  const diffInDays = Math.trunc(diffInMs / (1000 * 60 * 60 * 24));
  const aDays = Math.abs(diffInDays);
  if (aDays > 0) {
    const currentDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const dateDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const daysDiff = Math.trunc((dateDay.getTime() - currentDay.getTime()) / (1000 * 60 * 60 * 24));
    const aDaysDiff = Math.abs(daysDiff);
    if (aDaysDiff === 1) {
      return getHumanityCrement(daysDiff, HUMANITY_VALUE_FORMATS.day);
    } else {
      return getHumanityValue(daysDiff, HUMANITY_VALUE_FORMATS.day);
    }
  }

  let diffInHours = Math.trunc(diffInMs / (1000 * 60 * 60));
  const absDiffInHours = Math.abs(diffInHours);
  if (absDiffInHours > 0) {
    if (absDiffInHours === 1) {
      return getHumanityCrement(diffInHours, HUMANITY_VALUE_FORMATS.hour);
    } else {
      diffInHours = Math.round(diffInMs / (1000 * 60 * 60));
      return getHumanityValue(diffInHours, HUMANITY_VALUE_FORMATS.hour);
    }
  }

  let diffInMinutes = Math.trunc(diffInMs / (1000 * 60));
  const absDiffInMinutes = Math.abs(diffInMinutes);
  if (absDiffInMinutes > 0) {
    if (absDiffInMinutes === 1) {
      return getHumanityCrement(diffInMinutes, HUMANITY_VALUE_FORMATS.minute);
    } else {
      diffInMinutes = Math.round(diffInMs / (1000 * 60));
      return getHumanityValue(diffInMinutes, HUMANITY_VALUE_FORMATS.minute);
    }
  }

  const diffInSeconds = Math.trunc(diffInMs / (1000));
  const absDiffInSeconds = Math.abs(diffInSeconds);
  if (absDiffInSeconds > 0) {
    if (absDiffInSeconds === 1) {
      return getHumanityCrement(diffInSeconds, HUMANITY_VALUE_FORMATS.second);
    } else {
      return getHumanityValue(diffInSeconds, HUMANITY_VALUE_FORMATS.second);
    }
  }

  return 'только что';
}

function toDate(value: string | Date): Date {
  return value instanceof Date ? value : new Date(value);
}

@Component({
  selector: 'app-time-event',
  templateUrl: './time-event.component.html',
  styleUrls: ['./time-event.component.css']
})
export class TimeEventComponent implements OnInit, OnDestroy {

  @Input() value!: string | Date;

  interval: number | null = null;
  humanityValue!: string;

  constructor() {
  }

  ngOnInit(): void {
    this.updateHumanityValue();
    this.updateInterval();
  }

  ngOnDestroy(): void {
    if (this.interval !== null) {
      clearInterval(this.interval);
    }
  }

  format(): string {
    const date = toDate(this.value);
    return date.toLocaleString();
  }

  updateHumanityValue(): void {
    const date = toDate(this.value);
    const today = new Date();
    this.humanityValue = getHumanityDate(date, today);
  }

  updateInterval(): void {
    if (this.interval !== null) {
      clearInterval(this.interval);
    }
    const diffInMs = Math.abs(new Date().getTime() - toDate(this.value).getTime());
    const diffInDays = Math.trunc(diffInMs / (1000 * 60 * 60 * 24));
    if (diffInDays > 0) {
      return;
    }
    const diffInHours = Math.trunc(diffInMs / (1000 * 60 * 60));
    if (diffInHours > 0) {
      this.interval = setInterval(() => {
        this.updateHumanityValue();
        this.updateInterval();
      }, 1000 * 60 * 30);
      return;
    }
    const diffInMinutes = Math.trunc(diffInMs / (1000 * 60));
    if (diffInMinutes > 0) {
      this.interval = setInterval(() => {
        this.updateHumanityValue();
        this.updateInterval();
      }, 1000 * 30);
      return;
    }
    const diffInSeconds = Math.trunc(diffInMs / (1000));
    if (diffInSeconds >= 0) {
      this.interval = setInterval(() => {
        this.updateHumanityValue();
        this.updateInterval();
      }, 1000);
      return;
    }
  }

}
